import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { ArrowLeft, ArrowRight, ClipboardCheck } from 'lucide-react';
import QuestionCard from './QuestionCard';
import OptionButton from './OptionButton';

const AUDIT_SECTIONS = [
  {
    id: 'section1',
    title: 'Positionnement & réalité terrain',
    questions: [
      {
        id: 'situation',
        title: 'Situation actuelle',
        description: 'Où en es-tu dans ton projet ?',
        type: 'select',
        options: [
          { value: 'projet', label: 'Projet / réflexion' },
          { value: 'lancement', label: 'Lancement' },
          { value: 'irregulier', label: 'En activité mais irrégulier' },
          { value: 'stagnation', label: 'En stagnation' },
        ],
      },
      {
        id: 'objectif12mois',
        title: 'Objectif réaliste à 12 mois',
        description: 'Combien de logements vises-tu dans 12 mois ?',
        type: 'input',
        placeholder: 'Nombre de logements visés',
      },
      {
        id: 'tempsDisponible',
        title: 'Temps réel disponible par semaine',
        description: 'Combien d\'heures par semaine peux-tu réellement consacrer à cette activité ?',
        type: 'input',
        placeholder: 'Ex: 10 heures',
      },
    ],
  },
  {
    id: 'section2',
    title: 'Exécution & discipline',
    questions: [
      {
        id: 'suivrePlan',
        title: 'Capacité à suivre un plan',
        description: 'Je suis capable de suivre un plan sans me disperser',
        type: 'scale',
        min: 0,
        max: 5,
      },
      {
        id: 'inconfortable',
        title: 'Actions inconfortables',
        description: 'Je fais ce qui est inconfortable quand c\'est nécessaire (prospection, relances)',
        type: 'scale',
        min: 0,
        max: 5,
      },
      {
        id: 'repose',
        title: 'Structure actuelle',
        description: 'Aujourd\'hui, l\'activité repose principalement...',
        type: 'select',
        options: [
          { value: 'moi', label: 'Sur moi' },
          { value: 'prestataires', label: 'Sur quelques prestataires' },
          { value: 'process', label: 'Sur des process clairs' },
        ],
      },
    ],
  },
  {
    id: 'section3',
    title: 'Acquisition propriétaires',
    questions: [
      {
        id: 'canalPrincipal',
        title: 'Canal principal utilisé ou envisagé',
        description: 'Quel est ton canal d\'acquisition principal ?',
        type: 'select',
        options: [
          { value: 'reseau', label: 'Réseau personnel' },
          { value: 'terrain', label: 'Démarchage terrain' },
          { value: 'telephone', label: 'Téléphone' },
          { value: 'annonces', label: 'Annonces / plateformes' },
          { value: 'reseaux', label: 'Réseaux sociaux' },
          { value: 'nsp', label: 'Je ne sais pas encore' },
        ],
      },
      {
        id: 'tempsAcquisition',
        title: 'Temps pour l\'acquisition',
        description: 'Je peux consacrer 30 minutes par jour à l\'acquisition',
        type: 'scale',
        min: 0,
        max: 5,
      },
      {
        id: 'lister30',
        title: 'Liste de cibles',
        description: 'Je suis capable de lister 30 propriétaires ou cibles cette semaine',
        type: 'select',
        options: [
          { value: 'oui', label: 'Oui' },
          { value: 'non', label: 'Non' },
        ],
      },
    ],
  },
  {
    id: 'section4',
    title: 'Suivi & organisation',
    questions: [
      {
        id: 'suiviLeads',
        title: 'Suivi des leads',
        description: 'Aujourd\'hui, les leads sont suivis...',
        type: 'select',
        options: [
          { value: 'crm', label: 'Dans un CRM' },
          { value: 'tableur', label: 'Dans un tableur' },
          { value: 'whatsapp', label: 'Dans WhatsApp / notes' },
          { value: 'nulle', label: 'Nulle part / au feeling' },
        ],
      },
      {
        id: 'saisRelancer',
        title: 'Connaissance des relances',
        description: 'Sais-tu exactement qui relancer et quand ?',
        type: 'select',
        options: [
          { value: 'oui', label: 'Oui' },
          { value: 'non', label: 'Non' },
        ],
      },
      {
        id: 'sourceLeads',
        title: 'Source des leads',
        description: 'Sais-tu d\'où viennent tes leads ?',
        type: 'select',
        options: [
          { value: 'oui', label: 'Oui' },
          { value: 'non', label: 'Non' },
        ],
      },
    ],
  },
  {
    id: 'section5',
    title: 'Ressources & stabilité',
    questions: [
      {
        id: 'budget',
        title: 'Budget mobilisable',
        description: 'Budget mobilisable pour structurer l\'activité',
        type: 'select',
        options: [
          { value: '<1000', label: 'Moins de 1 000 €' },
          { value: '1000-3000', label: '1 000 à 3 000 €' },
          { value: '3000-6000', label: '3 000 à 6 000 €' },
          { value: '>6000', label: 'Plus de 6 000 €' },
        ],
      },
      {
        id: 'situationFinanciere',
        title: 'Situation financière actuelle',
        description: 'Comment qualifierais-tu ta situation financière ?',
        type: 'select',
        options: [
          { value: 'stable', label: 'Stable' },
          { value: 'variable', label: 'Variable' },
          { value: 'tension', label: 'En tension' },
        ],
      },
      {
        id: 'autreRevenu',
        title: 'Autre source de revenus',
        description: 'As-tu une autre source de revenus stable en parallèle ?',
        type: 'select',
        options: [
          { value: 'oui', label: 'Oui, revenus stables' },
          { value: 'partiel', label: 'Oui, mais irréguliers' },
          { value: 'non', label: 'Non, c\'est ma seule activité' },
        ],
      },
    ],
  },
  {
    id: 'section6',
    title: 'Cadre & sérieux',
    questions: [
      {
        id: 'experienceEntrepreneuriale',
        title: 'Expérience entrepreneuriale',
        description: 'As-tu déjà géré une activité ou un projet entrepreneurial ?',
        type: 'select',
        options: [
          { value: 'oui', label: 'Oui, j\'ai déjà entrepris' },
          { value: 'salarie', label: 'Non, j\'ai toujours été salarié' },
          { value: 'projet', label: 'J\'ai des projets en parallèle' },
        ],
      },
      {
        id: 'execution',
        title: 'Exécution',
        description: 'Si on me donne un plan clair, je l\'exécute avant de redemander',
        type: 'scale',
        min: 0,
        max: 5,
      },
    ],
  },
  {
    id: 'section7',
    title: 'Questions finales',
    questions: [
      {
        id: 'pourquoi',
        title: 'Ta motivation',
        description: 'Pourquoi tu veux faire ça, vraiment ? (3 phrases maximum)',
        type: 'textarea',
        placeholder: 'Exprime ta motivation en quelques phrases...',
      },
      {
        id: 'abandonner',
        title: 'Risque d\'abandon',
        description: 'Qu\'est-ce qui pourrait te faire abandonner ?',
        type: 'textarea',
        placeholder: 'Décris les obstacles potentiels...',
      },
    ],
  },
];

// Flatten questions for navigation
const getAllQuestions = () => {
  const questions = [];
  AUDIT_SECTIONS.forEach(section => {
    section.questions.forEach(q => {
      questions.push({ ...q, sectionTitle: section.title });
    });
  });
  return questions;
};

export default function AuditScreen({ preFilterData, onComplete, onBack }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  
  const allQuestions = getAllQuestions();
  const question = allQuestions[currentIndex];
  const progress = ((currentIndex + 1) / allQuestions.length) * 100;
  const isLastQuestion = currentIndex === allQuestions.length - 1;
  
  // Auto-initialize scale questions with default value
  React.useEffect(() => {
    if (question.type === 'scale' && answers[question.id] === undefined) {
      setAnswers(prev => ({ ...prev, [question.id]: 2 }));
    }
  }, [question.id, question.type, answers]);
  
  const canProceed = () => {
    const answer = answers[question.id];
    if (question.type === 'scale') return answer !== undefined;
    if (question.type === 'input' || question.type === 'textarea') return answer && answer.trim() !== '';
    return answer !== undefined;
  };

  const handleSelect = (value) => {
    setAnswers(prev => ({ ...prev, [question.id]: value }));
  };

  const handleInputChange = (e) => {
    setAnswers(prev => ({ ...prev, [question.id]: e.target.value }));
  };

  const handleSliderChange = (value) => {
    setAnswers(prev => ({ ...prev, [question.id]: value[0] }));
  };

  const handleNext = () => {
    if (isLastQuestion) {
      onComplete({ ...preFilterData, ...answers });
    } else {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    } else {
      onBack();
    }
  };

  const renderQuestionInput = () => {
    switch (question.type) {
      case 'select':
        return (
          <div className="space-y-3 mt-6">
            {question.options.map((option) => (
              <OptionButton
                key={option.value}
                label={option.label}
                selected={answers[question.id] === option.value}
                onClick={() => handleSelect(option.value)}
              />
            ))}
          </div>
        );
      
      case 'scale':
        return (
          <div className="mt-8 space-y-6">
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>0 - Pas du tout</span>
              <span>5 - Tout à fait</span>
            </div>
            <Slider
              value={[answers[question.id] ?? 2]}
              onValueChange={handleSliderChange}
              max={5}
              step={1}
              className="w-full"
            />
            <div className="flex justify-center">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary text-primary-foreground text-xl font-semibold">
                {answers[question.id] ?? 2}
              </span>
            </div>
          </div>
        );
      
      case 'input':
        return (
          <div className="mt-6">
            <Input
              value={answers[question.id] || ''}
              onChange={handleInputChange}
              placeholder={question.placeholder}
              className="h-12 text-base"
            />
          </div>
        );
      
      case 'textarea':
        return (
          <div className="mt-6">
            <Textarea
              value={answers[question.id] || ''}
              onChange={handleInputChange}
              placeholder={question.placeholder}
              className="min-h-[120px] text-base resize-none"
            />
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header with progress */}
      <header className="w-full border-b border-border bg-background sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ClipboardCheck className="w-4 h-4 text-primary" />
              <span className="text-sm text-muted-foreground">{question.sectionTitle}</span>
            </div>
            <span className="text-sm font-medium text-foreground">
              {currentIndex + 1} / {allQuestions.length}
            </span>
          </div>
          <Progress value={progress} className="h-1.5" />
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-xl w-full animate-fade-in" key={currentIndex}>
          <QuestionCard
            number={currentIndex + 1}
            title={question.title}
            description={question.description}
          >
            {renderQuestionInput()}
          </QuestionCard>
        </div>
      </div>

      {/* Footer with navigation */}
      <footer className="w-full border-t border-border bg-background sticky bottom-0">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={handlePrevious}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Button>
          
          <Button
            onClick={handleNext}
            disabled={!canProceed()}
            className="gap-2"
          >
            {isLastQuestion ? 'Voir les résultats' : 'Suivant'}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </footer>
    </div>
  );
}